import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Chip,
    useTheme,
    useMediaQuery,
    alpha,
    Stepper,
    Step,
    StepLabel,
    StepContent
} from '@mui/material';
import { motion } from 'framer-motion';
import KeyIcon from '@mui/icons-material/Key';
import SecurityIcon from '@mui/icons-material/Security';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LaunchIcon from '@mui/icons-material/Launch';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useNavigate } from 'react-router-dom';

// Colors matching your theme
const colors = {
    primary: "#0072E5",
    secondary: "#333333",
    accent: "#FF5722",
    lightGray: "#F8F9FA",
    darkGray: "#212529",
    white: "#FFFFFF",
    statsBg: "rgba(0, 114, 229, 0.05)"
};

// Section Title Component
const SectionTitle = ({ title, subtitle, align = "center", delay = 0.2 }) => {
    return (
        <Box
            component={motion.div}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay, duration: 0.5 }}
            sx={{
                mb: 5,
                textAlign: align,
                position: 'relative'
            }}
        >
            <Typography
                variant="h4"
                sx={{
                    fontWeight: "bold",
                    mb: 1.5,
                    fontFamily: '"Montserrat", sans-serif',
                    position: 'relative',
                    display: align === "center" ? 'inline-block' : 'block'
                }}
            >
                {title}
                <Box
                    sx={{
                        position: 'absolute',
                        height: 4,
                        width: 60,
                        backgroundColor: colors.accent,
                        bottom: -10,
                        left: align === "center" ? '50%' : 0,
                        transform: align === "center" ? 'translateX(-50%)' : 'none',
                        borderRadius: 2
                    }}
                />
            </Typography>
            {subtitle && (
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        maxWidth: align === "center" ? 700 : 'unset',
                        mx: align === "center" ? "auto" : 0,
                        mt: 3,
                        lineHeight: 1.6
                    }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
};

// Feature Card Component
const StepCard = ({ icon, title, description, image, imageAlt, delay = 0.3, warning, tip, code }) => {
    return (
        <Card
            component={motion.div}
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay, duration: 0.5 }}
            elevation={2}
            sx={{
                borderRadius: 4,
                overflow: 'hidden',
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.1)',
                }
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Box
                    sx={{
                        width: 70,
                        height: 70,
                        borderRadius: "50%",
                        bgcolor: alpha(colors.primary, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2.5,
                        transition: 'all 0.3s ease',
                    }}
                >
                    {React.cloneElement(icon, {
                        sx: {
                            fontSize: 32,
                            color: colors.primary,
                        }
                    })}
                </Box>

                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: "bold",
                        mb: 1.5,
                        fontFamily: '"Montserrat", sans-serif',
                    }}
                >
                    {title}
                </Typography>

                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6, mb: 2 }}
                >
                    {typeof description === 'string' ? (
                        <span dangerouslySetInnerHTML={{ __html: description }} />
                    ) : (
                        description
                    )}
                </Typography>

                {/* Real Image */}
                {image && (
                    <Box
                        sx={{
                            width: '100%',
                            borderRadius: 2,
                            overflow: 'hidden',
                            mb: 2,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        }}
                    >
                        <img
                            src={image}
                            alt={imageAlt}
                            style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                                border: '1px solid #eee'
                            }}
                            onError={(e) => {
                                // Fallback to placeholder if image fails to load
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        {/* Fallback placeholder */}
                        <Box
                            sx={{
                                width: '100%',
                                height: 200,
                                background: 'linear-gradient(45deg, #e3f2fd 25%, transparent 25%), linear-gradient(-45deg, #e3f2fd 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e3f2fd 75%), linear-gradient(-45deg, transparent 75%, #e3f2fd 75%)',
                                backgroundSize: '20px 20px',
                                border: '2px dashed #90caf9',
                                borderRadius: 2,
                                display: 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                            }}
                        >
                            <Typography variant="h4" sx={{ opacity: 0.5 }}>📷</Typography>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    bgcolor: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    p: 1,
                                    fontSize: '0.8rem'
                                }}
                            >
                                {imageAlt}
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Code example */}
                {code && (
                    <Paper
                        sx={{
                            bgcolor: '#1e1e1e',
                            color: '#f8f8f2',
                            p: 2,
                            borderRadius: 2,
                            fontFamily: 'monospace',
                            mb: 2,
                            position: 'relative'
                        }}
                    >
                        <Typography variant="caption" sx={{ color: '#888', mb: 1, display: 'block' }}>
                            API Key Example
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {code}
                        </Typography>
                    </Paper>
                )}

                {/* Warning */}
                {warning && (
                    <Paper
                        sx={{
                            bgcolor: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1
                        }}
                    >
                        <WarningIcon sx={{ color: '#856404', mt: 0.2 }} />
                        <Typography variant="body2" sx={{ color: '#856404' }}>
                            <strong>Important:</strong> {warning}
                        </Typography>
                    </Paper>
                )}

                {/* Tip */}
                {tip && (
                    <Paper
                        sx={{
                            bgcolor: '#d1ecf1',
                            border: '1px solid #bee5eb',
                            borderRadius: 2,
                            p: 2,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1
                        }}
                    >
                        <LightbulbIcon sx={{ color: '#0c5460', mt: 0.2 }} />
                        <Typography variant="body2" sx={{ color: '#0c5460' }}>
                            <strong>Tip:</strong> {tip}
                        </Typography>
                    </Paper>
                )}
            </CardContent>
        </Card>
    );
};

const StebbyApiGuide = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const steps = [
        {
            icon: <LaunchIcon />,
            title: "Access Stebby Admin Panel",
            description: "Log into your Stebby admin dashboard to manage API settings and integrations.",
            image: "/images/stebby/step1.png",
            tip: "If you don't have admin access, contact your Stebby account manager for the necessary permissions."
        },
        {
            icon: <IntegrationInstructionsIcon />,
            title: "Navigate to API Settings",
            description: (
                <>
                    Go to <strong>Sales and Services</strong>, choose <strong>Point Of Sale</strong> and click on <strong>Edit POS</strong>.
                </>
            ),
            image: "/images/stebby/step2.png",
            imageAlt: "Main dashboard with API settings menu highlighted"
        },
        {
            icon: <KeyIcon />,
            title: "Generate Your API Key",
            description: "Scroll down and click on API key toggle button. On Cash register system input, type <strong>IronTrack</strong> and click on " +
                "plus icon to generate your API key. Now you can copy your API key.",
            image: "/images/stebby/step3.png",
            code: "0b6aacbf-3fcc-4a23-9423-58f32cswc459",
            warning: "Keep your API key secure!"
        },
        {
            icon: <SecurityIcon />,
            title: "Add API key to IronTrack",
            description: "Go to <strong>My Affiliate</strong> page on Irontrack, click on <strong>Stebby API</strong> button and paste your API key in the modal. ",
            permissions: ["Read tickets", "Update ticket status", "Mark tickets as used"]
        },
        {
            icon: <IntegrationInstructionsIcon />,
            title: "Add to IronTrack",
            description: (
                <>
                    Go to <strong>Sales and Services</strong>, choose <strong>Point Of Sale</strong> and click on <strong>Edit POS</strong>. Click on <strong>Manage Point of Sale services</strong> Button.
                </>
            ),
            image: "/images/stebby/step5.png",
        },
        {
            icon: <CheckCircleIcon />,
            title: "Add Irontrack Plan ID to Stebby services",
            description: "On Irontrack page, go to Plans page and copy the plan ID you want to use with Stebby. " +
                " Paste it in the Stebby services API code field and click on green check button. <strong>Make sure to select the correct plan ID from Irontrack for your Stebby integration.</strong>" +
                " Every Stebby service needs correct plan ID from Irontrack! This will ensure that the correct plan is used for ticket redemption.",
            image: "/images/stebby/step6.png",
        }
    ];

    return (
        <Box sx={{ overflowX: 'hidden' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${colors.primary} 0%, #764ba2 100%)`,
                    color: 'white',
                    py: { xs: 8, md: 12 },
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        component={motion.div}
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                    >
                        <Typography
                            variant={isMobile ? "h3" : "h1"}
                            sx={{
                                fontWeight: 800,
                                mb: 3,
                                fontFamily: '"Montserrat", sans-serif',
                                lineHeight: 1.1
                            }}
                        >
                            🎫 Stebby API Integration Guide
                        </Typography>

                        <Typography
                            variant={isMobile ? "body1" : "h6"}
                            sx={{
                                mb: 4,
                                lineHeight: 1.6,
                                fontWeight: 400,
                                maxWidth: 800,
                                mx: "auto",
                                opacity: 0.9
                            }}
                        >
                            Step-by-step guide to connect your Stebby account with IronTrack for seamless ticket redemption
                        </Typography>


                    </Box>
                </Container>
            </Box>

            {/* Steps Section */}
            <Container sx={{ py: { xs: 6, md: 10 } }}>
                <SectionTitle
                    title="Setup Process"
                    subtitle="Follow these simple steps to integrate Stebby with your IronTrack affiliate account"
                />

                <Grid container spacing={4}>
                    {steps.map((step, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <StepCard
                                icon={step.icon}
                                title={`Step ${index + 1}: ${step.title}`}
                                description={step.description}
                                image={step.image}
                                imageAlt={step.imageAlt}
                                warning={step.warning}
                                tip={step.tip}
                                code={step.code}
                                delay={index * 0.1}
                            />


                        </Grid>
                    ))}
                </Grid>
            </Container>



            {/* Footer */}
            <Box sx={{ backgroundColor: colors.secondary, color: colors.white, py: 4 }}>
                <Container>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="body2" align="center">
                            © {new Date().getFullYear()} Irontrack OÜ | All rights reserved.
                        </Typography>
                        <Typography variant="body2" align="center" sx={{ opacity: 0.8 }}>
                            Reg. nr: 17184398 | Email: info@irontrack.ee
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                        color: alpha('#ffffff', 0.8)
                                    }
                                }}
                                onClick={() => navigate("/privacy-policy")}
                            >
                                Privacy Policy
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default StebbyApiGuide;